import { Injectable, inject } from '@angular/core';
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp, Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { McpConnectionsConfig, OrgIntegrationConfig } from '../models/integration.model';
import { FirebaseService } from './firebase.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private readonly firebase = inject(FirebaseService);

  async getConfig(orgId: string): Promise<McpConnectionsConfig> {
    const cacheKey = `crewdle_mcp_config_${orgId}`;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) return JSON.parse(stored) as McpConnectionsConfig;

    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'mcp-connections', 'config');
    const snapshot = await getDoc(configRef);
    const config: McpConnectionsConfig = snapshot.exists()
      ? { integrations: snapshot.data()['integrations'] || {} }
      : { integrations: {} };

    sessionStorage.setItem(cacheKey, JSON.stringify(config));
    return config;
  }

  subscribeToConfig(orgId: string, onChange: (config: McpConnectionsConfig) => void): Unsubscribe {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'mcp-connections', 'config');
    return onSnapshot(configRef, (snapshot) => {
      const config: McpConnectionsConfig = snapshot.exists()
        ? { integrations: snapshot.data()['integrations'] || {} }
        : { integrations: {} };
      sessionStorage.setItem(`crewdle_mcp_config_${orgId}`, JSON.stringify(config));
      onChange(config);
    });
  }

  async saveIntegrationConfig(orgId: string, integrationId: string, config: OrgIntegrationConfig): Promise<void> {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'mcp-connections', 'config');
    await setDoc(configRef, {
      integrations: { [integrationId]: config },
    }, { merge: true });
    sessionStorage.removeItem(`crewdle_mcp_config_${orgId}`);
  }

  async saveFullConfig(orgId: string, config: McpConnectionsConfig): Promise<void> {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'mcp-connections', 'config');
    await setDoc(configRef, config);
    sessionStorage.removeItem(`crewdle_mcp_config_${orgId}`);
  }

  async connectProvider(provider: string, orgId: string): Promise<string> {
    const getLoginToken = httpsCallable<
      { provider: string; organizationId: string },
      { loginToken: string }
    >(this.firebase.functions, 'getLoginToken');
    const result = await getLoginToken({ provider, organizationId: orgId });
    return result.data.loginToken;
  }

  async isProviderConnected(provider: string, orgId: string): Promise<boolean> {
    const check = httpsCallable<
      { provider: string; organizationId: string },
      { connected: boolean }
    >(this.firebase.functions, 'isProviderConnected');
    const result = await check({ provider, organizationId: orgId });
    return result.data.connected;
  }

  async disconnectProvider(provider: string, orgId: string): Promise<void> {
    const disconnect = httpsCallable<
      { provider: string; organizationId: string },
      void
    >(this.firebase.functions, 'disconnectProvider');
    await disconnect({ provider, organizationId: orgId });
  }

  async setupApiKeyProvider(provider: string, orgId: string, apiKey: string, siteId?: string): Promise<void> {
    const setup = httpsCallable<
      { provider: string; organizationId: string; apiKey: string; siteId?: string },
      void
    >(this.firebase.functions, 'setupProvider');
    await setup({ provider, organizationId: orgId, apiKey, ...(siteId ? { siteId } : {}) });
  }

  async setupCredentialsProvider(
    provider: string,
    orgId: string | null,
    email: string,
    password: string,
  ): Promise<void> {
    // Mirrors the MCP setup form flow: get a short-lived login token from
    // the Firebase callable, then POST `{ token, email, password }` to
    // `/setup/<provider>/connect` on the MCP server (same endpoint the
    // popup form submits to).
    const loginToken = orgId
      ? await this.connectProvider(provider, orgId)
      : await this.connectProviderForUser(provider);

    const response = await fetch(`${environment.mcpUrl}/setup/${provider}/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: loginToken, email, password }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `Setup failed (HTTP ${response.status})`);
    }
  }

  // -- User-level connection (per-user mode) --

  async connectProviderForUser(provider: string): Promise<string> {
    const getLoginToken = httpsCallable<
      { provider: string },
      { loginToken: string }
    >(this.firebase.functions, 'getLoginToken');
    const result = await getLoginToken({ provider });
    return result.data.loginToken;
  }

  async isProviderConnectedForUser(provider: string): Promise<boolean> {
    const check = httpsCallable<
      { provider: string },
      { connected: boolean }
    >(this.firebase.functions, 'isProviderConnected');
    const result = await check({ provider });
    return result.data.connected;
  }

  async disconnectProviderForUser(provider: string): Promise<void> {
    const disconnect = httpsCallable<
      { provider: string },
      void
    >(this.firebase.functions, 'disconnectProvider');
    await disconnect({ provider });
  }

  async setupApiKeyProviderForUser(provider: string, apiKey: string, siteId?: string): Promise<void> {
    const setup = httpsCallable<
      { provider: string; apiKey: string; siteId?: string },
      void
    >(this.firebase.functions, 'setupProvider');
    await setup({ provider, apiKey, ...(siteId ? { siteId } : {}) });
  }
}
